/**
 * BundleCreateModal — one asset, many placements.
 *
 * Opens from AdSlotManager. Admins pick a preset (Homepage Takeover /
 * Dashboard Blitz / …) or hand-check placements grouped by size, then
 * upload one image/video that fills every slot at once via
 * POST /api/admin/ad-campaigns/bundle.
 */
import React, { useContext, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import {
  X, Plus, Upload, ImageIcon as Image, FileVideo, Video, Sparkles, LayoutGrid,
  Monitor, Square, RectangleHorizontal,
} from 'lucide-react';

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

// Preset bundles — the ones admins reach for most.
const BUNDLE_PRESETS = [
  {
    id:    'homepage_takeover',
    label: 'Homepage Takeover',
    desc:  'Every top-of-funnel touchpoint on the landing page in one shot',
    placements: ['landing_hero', 'landing_ad_slot_1', 'landing_ad_slot_2', 'landing_footer'],
  },
  {
    id:    'dashboard_blitz',
    label: 'Dashboard Blitz',
    desc:  'Header, banner, sidebar, and footer on the member dashboard',
    placements: ['dashboard_header', 'dashboard_banner', 'dashboard_sidebar', 'dashboard_footer'],
  },
  {
    id:    'community_sweep',
    label: 'Community Sweep',
    desc:  'Forum + community feed for social-native audiences',
    placements: ['forum_header', 'forum_sidebar', 'forum_between', 'community_side'],
  },
  {
    id:    'lms_wrap',
    label: 'LMS Wrap',
    desc:  'Wraps the learning experience end-to-end',
    placements: ['learning_side', 'learning_between', 'course_footer', 'lesson_below'],
  },
  {
    id:    'store_takeover',
    label: 'Store Takeover',
    desc:  'Every merch surface — banner, featured, sidebar',
    placements: ['store_banner', 'store_featured', 'store_side'],
  },
];

const SIZE_MAP = {
  landing_hero: 'hero', landing_hero_content: 'hero', login_hero: 'hero', dashboard_welcome: 'hero',
  landing_ad_slot_1: 'banner', landing_ad_slot_2: 'banner', landing_features: 'banner', landing_footer: 'banner',
  login_footer: 'banner', dashboard_header: 'banner', dashboard_banner: 'banner', dashboard_footer: 'banner',
  database_header: 'banner', recipes_header: 'banner', business_header: 'banner', forum_header: 'banner',
  videos_header: 'banner', partners_header: 'banner', store_banner: 'banner', learning_between: 'banner',
  course_footer: 'banner', lesson_below: 'banner',
  login_sidebar: 'sidebar', dashboard_sidebar: 'sidebar', database_sidebar: 'sidebar', aichat_sidebar: 'sidebar',
  recipes_sidebar: 'sidebar', business_sidebar: 'sidebar', forum_sidebar: 'sidebar', community_side: 'sidebar',
  videos_sidebar: 'sidebar', learning_side: 'sidebar', live_hub_side: 'sidebar', store_side: 'sidebar',
  global_sidebar: 'sidebar', checkout_side: 'sidebar', profile_footer: 'sidebar',
  forum_between: 'inline', store_featured: 'inline', live_room_sponsor: 'inline',
  global_notification: 'overlay',
};

const SIZE_META = {
  hero:    { label: 'Hero',    icon: Monitor,               tone: 'text-amber-700' },
  banner:  { label: 'Banner',  icon: RectangleHorizontal,   tone: 'text-sky-700' },
  sidebar: { label: 'Sidebar', icon: Square,                tone: 'text-violet-700' },
  inline:  { label: 'Inline',  icon: LayoutGrid,            tone: 'text-emerald-700' },
  overlay: { label: 'Overlay', icon: Sparkles,              tone: 'text-rose-700' },
};

const groupOf = (p) => SIZE_MAP[p] || 'inline';

const BundleCreateModal = ({ open, onClose, placements, onCreated }) => {
  const { API } = useContext(AuthContext);
  const [selected, setSelected] = useState([]);
  const [presetId, setPresetId] = useState('');
  const [form, setForm] = useState({
    media_type: 'image',
    image_url: '',
    video_url: '',
    embed_url: '',
    title: '',
    description: '',
    click_url: '',
    cta_text: 'Learn more',
    priority: 'medium',
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const grouped = useMemo(() => {
    const g = { hero: [], banner: [], sidebar: [], inline: [], overlay: [] };
    for (const p of (placements || [])) (g[groupOf(p.value)] || (g[groupOf(p.value)] = [])).push(p);
    return g;
  }, [placements]);

  const applyPreset = (p) => {
    setPresetId(p.id);
    setSelected(p.placements);
    setForm((f) => ({ ...f, title: f.title || p.label }));
  };
  const toggle = (val) => {
    setPresetId('');
    setSelected((s) => (s.includes(val) ? s.filter((x) => x !== val) : [...s, val]));
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/admin/media-assets/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (form.media_type === 'video') setForm((f) => ({ ...f, video_url: r.data.url }));
      else setForm((f) => ({ ...f, image_url: r.data.url }));
      toast.success('Uploaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const submit = async () => {
    if (selected.length < 2) { toast.info('Pick at least 2 placements'); return; }
    const missing =
      (form.media_type === 'image' && !form.image_url.trim()) ||
      (form.media_type === 'video' && !form.video_url.trim()) ||
      (form.media_type === 'video_embed' && !form.embed_url.trim());
    if (missing) { toast.info('Add your creative URL'); return; }
    try {
      setSaving(true);
      const { data } = await axios.post(`${API}/admin/ad-campaigns/bundle`, {
        placements: selected,
        ...form,
      });
      toast.success(`Bundle live in ${data.campaign_count} slots`);
      onCreated && onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bundle failed');
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-navy-950/70 backdrop-blur-sm px-4" onClick={onClose} data-testid="bundle-create-modal">
      <div className="w-full max-w-3xl rounded-2xl bg-card border border-border shadow-2xl p-5 max-h-[92dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2 mb-3">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Bundle</div>
            <h3 className="font-heading text-lg font-extrabold text-foreground">Launch one campaign across many slots</h3>
          </div>
          <button type="button" onClick={onClose} className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Presets */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">1 · Pick a preset (or hand-pick below)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {BUNDLE_PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => applyPreset(p)}
                className={`text-left rounded-xl border p-3 transition-colors ${presetId === p.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40'}`}
                data-testid={`bundle-preset-${p.id}`}
              >
                <div className="font-bold text-sm text-foreground">{p.label}</div>
                <div className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{p.desc}</div>
                <div className="text-[10px] text-primary font-bold mt-1">{p.placements.length} slots</div>
              </button>
            ))}
          </div>
        </div>

        {/* Placement picker */}
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
            2 · Placements ({selected.length} selected)
          </div>
          <div className="rounded-xl border border-border max-h-72 overflow-y-auto p-2 space-y-3">
            {Object.entries(grouped).map(([g, list]) => {
              if (!list.length) return null;
              const meta = SIZE_META[g];
              const Icon = meta.icon;
              return (
                <div key={g}>
                  <div className={`text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1 ${meta.tone}`}>
                    <Icon className="w-3 h-3" /> {meta.label} · {list.length}
                  </div>
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                    {list.map((p) => (
                      <label key={p.value} className="flex items-center gap-2 text-xs rounded-md px-2 py-1 hover:bg-muted/40 cursor-pointer" data-testid={`bundle-placement-${p.value}`}>
                        <input
                          type="checkbox"
                          checked={selected.includes(p.value)}
                          onChange={() => toggle(p.value)}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="font-bold text-foreground">{p.label}</span>
                          <span className="block font-mono text-[10px] text-muted-foreground truncate">{p.value}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Creative */}
        <div className="mb-4 space-y-3">
          <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">3 · Creative</div>
          <div className="inline-flex rounded-full border border-border overflow-hidden text-[11px] font-bold w-full">
            {[
              { k: 'image',       l: 'Image',      icon: Image     },
              { k: 'video',       l: 'Video file', icon: FileVideo },
              { k: 'video_embed', l: 'Embed URL',  icon: Video     },
            ].map((m) => (
              <button
                key={m.k}
                type="button"
                onClick={() => setForm({ ...form, media_type: m.k })}
                className={`flex-1 h-9 px-2 uppercase tracking-widest transition-colors inline-flex items-center justify-center gap-1 ${form.media_type === m.k ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid={`bundle-media-${m.k}`}
              >
                <m.icon className="w-3 h-3" /> {m.l}
              </button>
            ))}
          </div>
          {form.media_type === 'image' && (
            <div className="flex gap-2">
              <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://…jpg" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-image-url" />
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadMedia(e.target.files?.[0])} className="hidden" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full font-bold whitespace-nowrap" data-testid="bundle-upload">
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          )}
          {form.media_type === 'video' && (
            <div className="flex gap-2">
              <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="/api/uploads/…mp4" className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-video-url" />
              <input ref={fileRef} type="file" accept="video/mp4,video/*" onChange={(e) => uploadMedia(e.target.files?.[0])} className="hidden" />
              <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full font-bold whitespace-nowrap">
                <Upload className="w-3.5 h-3.5 mr-1" /> {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </div>
          )}
          {form.media_type === 'video_embed' && (
            <input value={form.embed_url} onChange={(e) => setForm({ ...form, embed_url: e.target.value })} placeholder="https://youtube.com/…" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-embed-url" />
          )}
          <div className="grid grid-cols-2 gap-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Campaign title" className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-title" />
            <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} placeholder="CTA text" className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-cta" />
          </div>
          <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description shown alongside the creative" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-desc" />
          <div className="grid grid-cols-2 gap-2">
            <input value={form.click_url} onChange={(e) => setForm({ ...form, click_url: e.target.value })} placeholder="Click URL — /pricing" className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-click-url" />
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="bundle-priority">
              {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p} priority</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 pt-3 border-t border-border">
          <div className="text-[11px] text-muted-foreground">
            Bundling <b>{selected.length}</b> slot{selected.length === 1 ? '' : 's'} with 1 shared creative
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onClose} disabled={saving} className="rounded-full font-bold" data-testid="bundle-cancel">Cancel</Button>
            <Button size="sm" onClick={submit} disabled={saving || selected.length < 2} className="rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90" data-testid="bundle-submit">
              <Plus className="w-3.5 h-3.5 mr-1" /> {saving ? 'Launching…' : `Launch bundle · ${selected.length}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BundleCreateModal;
